/// <reference path="../../Frontend.Core/dist/definitions/NCB.AlternativeHotels.d.ts" />

import { observable, action, runInAction, computed, autorun, reaction, toJS } from "mobx";
import { AppScroll } from "./components/shared/appScroll";
import addonstore from "./stores/addonStore";
import roomstore from "./stores/roomStore";

export interface IInitialWizardState {
    startDate: Date,
    endDate: Date,
    rooms: CC.Library.Models.Calendar.NcbCalendarRoom[],
    showCalendar: boolean,
    groupCode: string,
    promotionCode: string
}

export enum IAppMode {
    Wizard,
    Confirmation,
    Login,
    Reservations,
    LookupReservation,
    Error,
    Widget
}

class AppState {

    private mobileWidth = 767;
    private doScrollToTop = false;

    appScroll: AppScroll;
    isNonRoom: boolean;

    guestHasChangedRoom: boolean;

    @observable reservations: CC.Library.Models.Shared.NcbHotelReservationRq[];
    @observable startDate: Date;
    @observable endDate: Date;
    @observable promotionCode: string;
    @observable currency: string;
    @observable modifyConfirmationNumber: string;
    @observable confirmationToken: string;

    // Wizard states
    @observable appMode: IAppMode;
    @observable roomIndex: number;
    @observable doShowCalendarPage: boolean;
    @observable ratePlanOrGroupCode: string;
    @observable doShowRoomDetails: boolean;
    @observable windowWidth: number;
    @observable isMobile: boolean;
    @observable loyaltyOptOut: boolean;
    
    @computed get isAddonsAvailable(): boolean {
        if (addonstore.cache[this.roomIndex] == null)
            return false;

        let status = addonstore.cache[this.roomIndex].Addons.length > 0;

        console.log("--- isAddonsAvailable", status);

        return status;
    }

    // The current roomindex rate code
    @computed get rateCode(): string | null {
        if (this.reservations[this.roomIndex] == null || this.reservations[this.roomIndex].Rooms[0] == null)
            return null;

        return this.reservations[this.roomIndex].Rooms[0].RatePlan;
    }

    // The current roomindex room type code
    @computed get roomTypeCode(): string | null {
        if (this.reservations[this.roomIndex] == null || this.reservations[this.roomIndex].Rooms[0] == null)
            return null;

        return this.reservations[this.roomIndex].Rooms[0].RoomType;
    }

    @computed get numNights(): number {
        if (this.startDate == null || this.endDate == null)
            return -1;

        return NCB.DateUtils.getNumNights(this.startDate, this.endDate);
    }

    @computed get roomGroupCode(): NCB.FeatureMatrix.IGroup {
        return NCB.FeatureMatrix.getGroupFromRoomType(this.roomTypeCode);
    }

    /* Get the chepast price available for the selected group */
    @computed get basePriceRoom(): CC.Library.Models.Availability.AvailabilityRoomRate {
        return this.getCachedRoom(this.roomIndex);
    }

    /* Get the current upgradable rooms as a tree structure */
    @computed get roomTree(): NCB.RoomHelpers.IRoomTree[] {
        return NCB.RoomHelpers.getRoomTree(this.roomGroupCode.Id);
    }

    @computed get roomParents(): string[] {
        return NCB.RoomHelpers.getRoomCodeParents(this.roomTypeCode);
    }

    @computed get currentRoom(): CC.Library.Models.Availability.AvailabilityRoomRate | null {
        if (roomstore.isLoading || roomstore.cache[this.roomIndex] == null)
            return null;

        return roomstore.cache[this.roomIndex].Rooms.filter(x => x.RatePlan.Code === this.rateCode && x.RoomType.Code === this.roomTypeCode)[0];
    }

    @computed get useAlternativeHotel(): boolean {
        if (this.promotionCode != null && this.promotionCode.length > 0)
            return false;

        let rateParam = NCB.Utils.getParameterFromURL('rate');
        if (rateParam != null && rateParam.length > 0)
            return false;

        let promoParam = NCB.Utils.getParameterFromURL('promotionCode');
        if (promoParam!= null && promoParam.length > 0)
            return false;

        return  NCB.AlternativeHotels.hasAlternativeHotels();
    }

    @computed get reservationRequiresLoyaltyMember(): boolean {
        if (NCB.Settings.UseChoiceLoyalty === false || this.reservations == null)
            return false;
        
        let rateCodes = this.reservations.map(x => x.Rooms[0].RatePlan);
        let requireMembership = false;

        rateCodes.forEach(r => {
            let rf = NCB.FeatureMatrix.getRate(r);
            if (rf != null && rf.IsLoyaltyRate === true) {
                requireMembership = true;
            }
        });

        return requireMembership;
    }

    @computed get numWizardSteps (): number {
        let steps = 2;

        if (NCB.Settings.ShowPackagesInWizard === true)
            steps++;

        if (NCB.Settings.NumAddonSteps > 0) {
            if (addonstore.isLoading || this.isAddonsAvailable)
                steps += NCB.Settings.NumAddonSteps;
        }

        //if (!this.isNonRoom)
        //    steps++;

        return steps;
    }

    constructor() {
        
        this.setStartDate = this.setStartDate.bind(this);
        this.setEndDate = this.setEndDate.bind(this);
        this.setRoom = this.setRoom.bind(this);
        this.navigateToHeader = this.navigateToHeader.bind(this);
        this.navigateToCalendar = this.navigateToCalendar.bind(this);
        this.navigateToRoom = this.navigateToRoom.bind(this);
        this.navigateToAddons = this.navigateToAddons.bind(this);
        this.navigateToCheckout = this.navigateToCheckout.bind(this);
        this.setGuestCount = this.setGuestCount.bind(this);
        this.selectCurrency = this.selectCurrency.bind(this);
        this.getCachedRoom = this.getCachedRoom.bind(this);
        this.checkCartIntegrity = this.checkCartIntegrity.bind(this);
        this.getIntialStateFromUrl = this.getIntialStateFromUrl.bind(this);

        (window as any).ncb_appstate = this;

        this.appScroll = new AppScroll();

        this.appMode = this.initializeAppMode();

        let initialState = this.getIntialStateFromUrl();

        this.modifyConfirmationNumber = "";
        this.promotionCode = initialState.promotionCode;
        this.startDate = initialState.startDate;
        this.endDate = initialState.endDate;
        this.doShowCalendarPage = initialState.showCalendar;
        this.ratePlanOrGroupCode = initialState.groupCode;

        this.guestHasChangedRoom = false;
        this.loyaltyOptOut = false;

        this.reservations = [NCB.Cart.createReservation({
            PromotionCode: null,
            StartDate: this.startDate == null ? null : NCB.DateUtils.getISODate(this.startDate),
            EndDate: this.endDate == null ? null : NCB.DateUtils.getISODate(this.endDate),
            RatePlan: null,
            RoomType: null,
            Guests: initialState.rooms[0].Guests
        })];

        initialState.rooms.forEach((r, i) => {
            if (i > 0) {
                this.addRoom(r.Guests);
            }
        });

        initialState.rooms.forEach((r, i) => {
            roomstore.loadRooms(i,
                this.startDate,
                this.endDate,
                this.reservations[i].Rooms[0].Guests,
                this.promotionCode,
                this.ratePlanOrGroupCode);
        });
        
        this.roomIndex = 0;
        this.windowWidth = window.innerWidth;
        this.isMobile = (window.innerWidth <= this.mobileWidth);
        this.currency = NCB.Settings.Currency;

        // Wizard states    
        this.doShowRoomDetails = true;

        window.addEventListener('resize', this.resizeCallback);

        const roomReaction = reaction(
            () => {
                return {
                    numRooms: this.reservations.length,
                    startDate: this.startDate,
                    endDate: this.endDate,
                    guests: this.reservations.map(x => x.Rooms[0].Guests),
                    promotionCode: this.promotionCode,
                    ratePlanOrGroupCode: this.ratePlanOrGroupCode
                }
            },
            () => this.reservations.forEach((r, i) => {
                roomstore.loadRooms(i,
                    this.startDate,
                    this.endDate,
                    r.Rooms[0].Guests,
                    this.promotionCode,
                    this.ratePlanOrGroupCode);
            })
        )

        const addonReaction = reaction(
            () => {
                return this.reservations.map((x, i) => {
                    return {
                        roomIndex: i,
                        startDate: this.startDate,
                        endDate: this.endDate,
                        guests: x.Rooms[0].Guests,
                        promotionCode: this.promotionCode,
                        ratePlanOrGroupCode: this.ratePlanOrGroupCode,
                        rooms: x.Rooms[0].RoomType,
                        rate: x.Rooms[0].RatePlan
                    }
                });
            },
            reaction => {
                return this.reservations.forEach((r, i) => {
                    addonstore.loadAddons(i,
                        this.startDate,
                        this.endDate,
                        r.Rooms[0].RatePlan,
                        r.Rooms[0].RoomType,
                        this.promotionCode,
                        r.Rooms[0].Guests);
                })
            }
        );

        NCB.Utils.checkBackendStatus(() => {
            runInAction(() => {
                this.appMode = IAppMode.Error;
            });
        });
    }

    initializeAppMode = (): IAppMode => {
        let mode: IAppMode = IAppMode.Wizard;
        let session = NCB.Session.getSession();

        location.search.substr(1).split("&").forEach((item) => {
            let s = item.split("=");
            let key = s[0];
            let value = s[1];
            
            switch (key) {
                case "start":
                    if (value == 'newres') {
                        NCB.Session.resetReservationResponse();
                        mode = IAppMode.Wizard;
                    }
                    break;

                case "searchres":
                    if (session != null && session.reservations != null)
                        mode = IAppMode.Reservations;
                    else
                        mode = IAppMode.Login
                    break;

                case "transactionId":
                    mode = IAppMode.Confirmation;
                    break;

                case "confirmation":
                    mode = IAppMode.Confirmation;
                    break;

                case "lookup":
                    mode = IAppMode.LookupReservation;
                    break;

                case "nonroom":
                    this.isNonRoom = true;

            }
        });

        if (NCB.Settings.FeatureMatrixConfiguration.DoShowErrorPage === true) {
            mode = IAppMode.Error;
        }

        if (this.isNonRoom)
            NCB.Settings.CalendarRateFilterCode = ""; // TODO: Set this to a non-room-specific filter code

        // Handle the case when the guest navigates away from the payment process
        // This will show the confirmation page instead of starting a new booking process
        if (mode === IAppMode.Wizard && NCB.Session.getSession() != null && NCB.Session.getSession().createReservationResponse != null) {
            mode = IAppMode.Confirmation;
        }

        return mode;
    }

    resizeCallback = () => {
        // Is this mobile width?
        let newState = window.innerWidth <= this.mobileWidth;

        if (this.isMobile !== newState) {
            runInAction(() => {
                this.isMobile = newState;
                this.windowWidth = window.innerWidth;
            });
        } else {
            runInAction(() => this.windowWidth = window.innerWidth);
        }
    }

    @action modifyReservation = (modifyRes: CC.Library.Models.Shared.NcbHotelReservationRs, showCalendar: boolean) => {
        let res = toJS(modifyRes);
        let room = res.Rooms[0];

        this.modifyConfirmationNumber = res.ConfirmationNr;
        this.confirmationToken = res.ConfirmationToken;
        this.guestHasChangedRoom = true;

        let services: CC.Library.Models.Shared.NcbServiceRq[] = [];

        res.Services.forEach(x => {
            let timeSplit = x.TimeSpan.split("T");

            switch (x.ServicePricingType) {
                case CC.Library.Helpers.PricingTypes.PER_STAY_PRICING:
                case CC.Library.Helpers.PricingTypes.PER_NIGHT_PRICING:
                case CC.Library.Helpers.PricingTypes.PER_PERSON_PER_NIGHT_PRICING:
                case CC.Library.Helpers.PricingTypes.PER_AGEGROUP_PER_NIGHT_PRICING:

                    // Remove duplicates in the case of Per Night Pricing
                    if (services.map(s => s.ServiceCode).indexOf(x.ServiceCode) > -1)
                        return;

                    // No date for Per Stay Pricing, but keep time
                    timeSplit[0] = "";
                        
                    break;
            }

            services.push({
                Guests: x.Guests,
                Date: timeSplit[0] || "",
                ServiceCode: x.ServiceCode,
                StartTime: timeSplit.length > 1 ? "T" + timeSplit[1] : ""
            });
        });

        let rooms: CC.Library.Models.Shared.NcbRoomStayRq[] = [
            {
                PromotionCode: room.PromotionCode,
                EndDate: room.EndDate,
                Guests: room.Guests,
                RatePlan: room.RatePlan,
                RoomType: room.RoomType,
                StartDate: room.StartDate
            }
        ];

        this.promotionCode = room.PromotionCode;
        this.startDate = new Date(Date.parse(room.StartDate)),
        this.endDate = new Date(Date.parse(room.EndDate));

        this.reservations = [
            {
                Comment: res.Comment,
                Customer: res.Customer,
                IataCode: "",
                Payment: res.Payment,
                Rooms: rooms,
                Services: services,
                UniqueId: ""
            }
        ];
        
        this.doShowCalendarPage = showCalendar;

        console.log("MODIFYRES", toJS(this.reservations));

        this.appMode = IAppMode.Wizard;
    }

    @action setAppMode = (mode: IAppMode) => {
        this.appMode = mode;
    }

    @action
    public setCalendar = (startDate: Date, endDate: Date, showCalendar: boolean = false, promotionCode?: string, rooms?: CC.Library.Models.Calendar.NcbCalendarRoom[], groupCode?: string) => {
        // Update the cart with the new guestcount
        if (this.reservations != null && this.reservations.length > 0) {
            for (let i = 0; this.reservations.length > i; i++) {

                // We have to re-find the price for this room
                // reservations[i].setCalculatedPrice(-1);

                let room: CC.Library.Models.Shared.NcbRoomStayRq = toJS(this.reservations[i].Rooms[0]);

                room.StartDate = NCB.DateUtils.getISODate(startDate);
                room.EndDate = NCB.DateUtils.getISODate(endDate);

                if (rooms != null) {
                    room.Guests = rooms[i].Guests
                }

                // Reset the rest of the rooms since they need to be configured again
                if (i > 0) {
                    room.RatePlan = null;
                    room.RoomType = null;

                    roomstore.cache[i] = null;
                }

                // Force reaction from mobx
                this.reservations[i].Rooms[0] = room;
            }
        }

        if (!showCalendar && this.isMobile)
            window.scroll(0, 50); // TODO: Calculate the Y-value instead of using a hard-coded value

        this.promotionCode = promotionCode != null ? promotionCode : this.promotionCode;
        this.startDate = startDate;
        this.endDate = endDate;
        this.doShowCalendarPage = showCalendar;
        this.ratePlanOrGroupCode = groupCode != null ? groupCode : this.ratePlanOrGroupCode;

        // Multiple rooms
        if (this.reservations.length > 1) {

            // Force the wizard to find a new first room
            if (this.roomIndex !== 0)
                this.changeRoomIndex(0);

            this.doShowRoomDetails = true;
        }

        if (showCalendar)
            document.body.classList.add("ncb-wizard-calendar-page");
        else
            document.body.classList.remove("ncb-wizard-calendar-page");
    }

    public setStartDate(date: Date) {
        this.setCalendar(date, this.endDate);
    }

    public setEndDate(date: Date) {
        this.setCalendar(this.startDate, date);
    }

    @action setGuestCount(guests: CC.Library.Models.Shared.NcbGuestCount[], index: number = 0): void {
        this.reservations[index].Rooms[0].Guests = guests;
    }

    @action public setRoom(roomIndex: number, roomTypeCode: string, rateCode: string): boolean {
        let foundRoom = false;

        roomstore.cache[roomIndex].Rooms.forEach((x) => {
            if (x.RoomType.Code === roomTypeCode && x.RatePlan.Code === rateCode) {
                foundRoom = true;
            }
        });

        if (foundRoom) {
            let r = this.reservations[roomIndex].Rooms[0];
            r.PromotionCode = this.promotionCode;
            r.StartDate = NCB.DateUtils.getISODate(this.startDate);
            r.EndDate = NCB.DateUtils.getISODate(this.endDate);
            r.RatePlan = rateCode;
            r.RoomType = roomTypeCode;
            
            roomstore.sendMetrics("" + appstate.roomIndex + rateCode + roomTypeCode);
        }

        // Reset loyaltyOptOut?
        if (this.loyaltyOptOut) {
            let rateFeatures = NCB.Settings.FeatureMatrixConfiguration.Rates.filter(x => x.Codes.indexOf(rateCode) > -1);
            if (rateFeatures.some(x => x.IsLoyaltyRate)) {
                this.loyaltyOptOut = false;
            }
        }

        return foundRoom;
    }

    public setRate(roomIndex: number, rateCode: string) {
        this.setRoom(roomIndex, this.roomTypeCode, rateCode);
    }

    toggleCalendar = (doShow: boolean = true) => {
        if (doShow) {
            this.doShowCalendarPage = true;
            document.body.classList.add("ncb-wizard-calendar-page");
        } else {
            document.body.classList.remove("ncb-wizard-calendar-page");
        }
    }

    public navigateToHeader() {

        let posY = 0;
        if (this.reservations != null && this.reservations.length > 1) {
            posY = document.querySelector('#NCB-app .ncb-wizard-room[data-room-index="'+this.roomIndex+'"]').getBoundingClientRect().top + window.pageYOffset - 100;            
        } else {
            posY = document.getElementById("NCB-app").getBoundingClientRect().top + window.pageYOffset;
        }

        this.appScroll.scrollTo(Math.round(posY));
    }

    @action
    public navigateToCalendar(promotionCode?: string) {
        
        if (promotionCode != null)
            this.promotionCode = promotionCode;

        this.toggleCalendar();
    }

    public navigateToRoom(skipAnimation?: boolean) {
        let posY = document.getElementById("ncb-page-room").getBoundingClientRect().top + window.pageYOffset;

        if (skipAnimation === true) {
            this.appScroll.halt();
            window.scrollTo(0, posY);

            // Force the header to re-render
            window.dispatchEvent(new Event('ncb-header-refresh'));

        } else {
            this.appScroll.scrollTo(posY);
        }
    }

    public navigateToAddons(addonStepIndex: number) {
        let posY: number;

        let id = "ncb-page-addons-" + addonStepIndex;

        if (this.isMobile) {
            posY = document.getElementById(id).getBoundingClientRect().top + window.pageYOffset;
        } else {
            posY = document.getElementById(id).getBoundingClientRect().top + window.pageYOffset;
        }

        this.appScroll.scrollTo(posY);
    }

    public navigateToCheckout() {
        let posY;

        if (this.isMobile)
            posY = document.getElementById("ncb-page-checkout").getBoundingClientRect().top + window.pageYOffset;
        else
            posY = document.getElementById("ncb-page-checkout").getBoundingClientRect().top + window.pageYOffset;

        this.appScroll.scrollTo(posY);
    }

    @action
    changeRoomIndex(newIndex: number) {

        // Load the room state from the cart
        let cartRoom = this.reservations[newIndex];
        let room = cartRoom.Rooms[0];

        if (room.RoomType == null) {
            // Switching to a room that still is not initialized
            this.doShowCalendarPage = false;
            this.roomIndex = newIndex;

            roomstore.loadRooms(newIndex,
                    this.startDate,
                    this.endDate,
                    cartRoom.Rooms[0].Guests,
                    this.promotionCode,
                    this.ratePlanOrGroupCode);

        } else {
            let roomFeature = NCB.FeatureMatrix.getRoom(room.RoomType);

            if (cartRoom == null) {
                NCB.Log.error(new Error("CustomError"), "WizardProxy could not get change room index", { newIndex: newIndex });
                return;
            }
            if (roomFeature == null) {
                NCB.Log.error(new Error("CustomError"), "WizardProxy could not get change room index because roomFeature was not found", { roomType: room.RoomType });
                return;
            }

            this.roomIndex = newIndex;
            //this.startDate = new Date(Date.parse(room.StartDate));
            //this.endDate = new Date(Date.parse(room.EndDate));
            this.doShowCalendarPage = false;
        }

        // Scroll to the next room when the guest switches between rooms
        if (this.doShowRoomDetails && this.reservations != null && this.reservations.length > 1) {
            /* console.log("appstate.changeRoomIndex", newIndex);

            let qs = document.querySelector('.ncb-wizard-room[data-room-index="' + this.roomIndex + '"]');
            if (qs != null) {
                let posY = qs.getBoundingClientRect().top + window.pageYOffset;
            
                // Approximate the header
                posY -= 100;
                
                this.appScroll.scrollTo(posY);
            }
            */

           this.doScrollToTop = true;
        } 
    }

    /**
     * 
     * @param guestCount 
     * @return The new reservation index
     */
    @action addRoom(guestCount: CC.Library.Models.Shared.NcbGuestCount[]): number {

        // Copy the details from the first room
        let firstRoom = this.reservations[0].Rooms[0];

        let newRoom: CC.Library.Models.Shared.NcbRoomStayRq = null;

        if (firstRoom != null) {
            newRoom = {
                PromotionCode: this.promotionCode,
                StartDate: firstRoom.StartDate,
                EndDate: firstRoom.EndDate,
                RatePlan: firstRoom.RatePlan,
                RoomType: firstRoom.RoomType,
                Guests: guestCount
            }
        } else {
            newRoom = {
                PromotionCode: this.promotionCode,
                StartDate: null,
                EndDate: null,
                RatePlan: null,
                RoomType: null,
                Guests: guestCount
            }
        }

        let newReservation = NCB.Cart.createReservation(newRoom);
        this.reservations.push(newReservation);

        return this.reservations.length - 1;
    }

    @action
    public removeRoom(roomIndex: number) {
        if (this.reservations.length < 2) {
            console.warn("Can not remove the last room");

            return;
        }

        let indexBeforeDelete = this.roomIndex;

        if (indexBeforeDelete !== 0)
            this.changeRoomIndex(0);

        this.reservations.splice(roomIndex, 1);

        addonstore.removeRoom(roomIndex);
        roomstore.cache.splice(roomIndex, 1);
    }

    @action selectCurrency(newCurrency: string) {
        NCB.Log.event("Change Currency", {
            selectedCurrency: newCurrency
        },
            {
                "Change Currency": 1
            }
        );

        this.currency = newCurrency;
    }

    public getGuaranteeCode(roomIndex: number): string | null {
        let r = this.reservations[roomIndex];
        if (r != null) {
            let cache = roomstore.cache[roomIndex];
            if (cache != null) {
                let candidates = cache.Rooms.filter(cr => cr.RatePlan.Code === r.Rooms[0].RatePlan && cr.RoomType.Code === r.Rooms[0].RoomType);
                if (candidates[0] == null) {
                    NCB.Log.customError("appstate.getGuaranteeCode: Could not find code", { roomIndex: roomIndex, res: toJS(this.reservations[roomIndex]) });
                    return null;
                }

                return candidates[0].RatePlan.Guarantee.Code
            }
        }

        return null;
    }

    public isRatePrepayment(roomIndex: number): boolean {
        let cr = this.getCachedRoom(roomIndex);

        return NCB.Settings.FeatureMatrixConfiguration.PrepaymentGuaranteeCodes.indexOf(cr.RatePlan.Guarantee.Code) > -1;
    }

    public isRateInvoice(roomIndex: number): boolean {
        let cr = this.getCachedRoom(roomIndex);

        return NCB.Settings.FeatureMatrixConfiguration.InvoiceGuaranteeCodes.indexOf(cr.RatePlan.Guarantee.Code) > -1;
    }

    /* Returns the Cart's room if no Rate or Room is defined */
    public getCachedRoom(roomIndex: number, rateCode?: string, roomCode?: string, anyRoomTypeIfNotFound?: boolean): CC.Library.Models.Availability.AvailabilityRoomRate | null {
        try {
            let c = this.reservations[roomIndex];
            let r = c.Rooms[0];

            if (roomstore.cache[roomIndex] == null)
                return null;

            let compareRate = (rateCode == null) ? r.RatePlan : rateCode;
            let compareRoom = (roomCode == null) ? r.RoomType : roomCode;

            let rc = roomstore.cache[roomIndex].Rooms.filter(x => x.RatePlan.Code === compareRate && x.RoomType.Code === compareRoom);

            if (rc.length === 0 && anyRoomTypeIfNotFound === true) {
                rc = roomstore.cache[roomIndex].Rooms.filter(x => x.RatePlan.Code === compareRate);
            }

            return rc[0];
        } catch (e) {
            NCB.Log.customError("appstate.getCachedRoom", { roomIndex: roomIndex, exception: e });
        }

        return null;
    }

    /**
     * Check if the the new availability result matches the addons that may reside in the NCB.Cart
     * Returns an explanation if anything was changed
     */
    checkCartIntegrity(roomIndex: number): string | null {

        let reservation = this.reservations[roomIndex];
        let addons = reservation.Services

        if (addons == null || addons.length === 0)
            return;

        let compareAddons = addonstore.cache[roomIndex];
        if (compareAddons == null || compareAddons.Addons == null) {
            NCB.Log.customError("checkCartIntegrity() could not find any addons", null);
            return;
        }

        // Step 1 - find all unavailable addons and remove them from the cart
        let missingAddons: CC.Library.Models.Shared.NcbServiceRq[] = [];
        addons.forEach((x) => {
            let availAddonCandidates = compareAddons.Addons.filter((a) => {
                return (a.Code === x.ServiceCode);
            });

            if (availAddonCandidates.length === 0) {
                missingAddons.push(x);
                return;
            } else if (availAddonCandidates.length > 1) {
                NCB.Log.error(new Error("CustomError"), "checkCartIntegrity(): found multiple addon candidates", { serviceCode: x.ServiceCode });
                return;
            }
        });

        if (missingAddons.length > 0) {
            missingAddons.forEach((x) => {
                console.log("Removing addon", x.ServiceCode);
                this.removeService(roomIndex, x.ServiceCode);
            });
        }

        // Step 2 - match the selected dates and times so that the addons in the cart is actually available
        addons = this.reservations[roomIndex].Services;
        addons.forEach((x) => {

            let availAddons = compareAddons.Addons.filter((a) => { return a.Code === x.ServiceCode });
            if (availAddons.length !== 1) {
                NCB.Log.error(new Error("CustomError"), "checkCartIntegrity(): could not find addon for date/time adjustment", { serviceCode: x.ServiceCode });
                return;
            }

            // We have found the addon, now try to match date and time to the new availability result
            let addon = availAddons[0];

            // Is this the identical date/time?
            let dateMatch = false;
            let timeMatch = false;
            let quantityMatch = false;

            addon.AddonDates.forEach((ad) => {
                if (ad.DateData === x.Date) {
                    dateMatch = true;

                    ad.AddonTimes.forEach((at) => {
                        if (at.TimeData === x.StartTime) {
                            timeMatch = true;

                            if (at.Quantity >= (NCB.Utils.getNumAdults(x.Guests) + NCB.Utils.getNumChildren(x.Guests))) {
                                quantityMatch = true;
                            }
                        }
                    });
                }
            });

            // If an exact match is found, stop processing this addon
            let foundExactMatch = dateMatch && timeMatch && quantityMatch;

            if (foundExactMatch) {
                return;
            }

            // Ok, addon does not match exactly.
            this.removeService(roomIndex, x.ServiceCode);
            missingAddons.push(x);
        });

        // Notify the user about the removed addons
        let alertMessage: string = null;
        if (missingAddons.length > 0) {
            let message = missingAddons.map((x) => {
                let serviceFeature = NCB.FeatureMatrix.getAddonFeature(x.ServiceCode);
                let serviceName = x.ServiceCode;
                if (serviceFeature != null && serviceFeature.Name != null) {
                    serviceName = NCB.FeatureMatrix.translate(serviceFeature.Name);
                    if (serviceName == null || serviceName.length < 1)
                        serviceName = x.ServiceCode;
                }
                return (x.Date != null && x.Date.length > 0) ? "■ " + serviceName + " (" + x.Date + ")" : "■ " + serviceName;
            });

            alertMessage = NCB.I18n.T.ADDONS_NOT_AVAILABLE_FOR_DATE_MESSAGE + "\n\n" + message.join("\n");
        }

        return alertMessage;
    }


    @computed get totalPrice(): number[] {
        let totalPrice = new Array(this.reservations.length);
        let numNights = this.numNights;

        for (let index=0; this.reservations.length > index; index++) {
            totalPrice[index] = this.getRoomPrice(index);
            
            // Calculate addon total
            if (addonstore.cache[index] != null) {
                this.reservations[index].Services.forEach((s) => {
                    let foundAddon = addonstore.cache[index].Addons.filter(a => a.Code === s.ServiceCode)[0];
                    if (foundAddon == null)  {
                        NCB.Log.customError("checkoutPage.render(): Could not find addon", {addon: s});
                        return;
                    }

                    let foundAddonDate = foundAddon.AddonDates.filter(d => d.DateData.length === 0 || d.DateData === s.Date);
                    if (foundAddonDate.length === 0) {
                        NCB.Log.customError("checkoutPage.render(): Could not find addon date", {foundAddon: toJS(foundAddon), service: toJS(s)});
                    } else {
                        let foundAddonTime = foundAddonDate[0].AddonTimes.filter(t => t.TimeData.length === 0 || t.TimeData === s.StartTime);
                        if (foundAddonTime.length === 0) {
                            NCB.Log.customError("checkoutPage.render(): Could not find addon time", {time: s.StartTime, addon: s});
                        } else {
                            let addonTotal = NCB.AddonHelper.getTotalPriceFromType(foundAddonTime[0].Price, s.Guests, numNights);
                            totalPrice[index] += addonTotal;
                        }
                    }                    
                });
            }
        }

        return totalPrice;
    }

    getRoomAddonTotalPrice = (index: number): number => {
        let numNights = this.numNights;
        let totalPrice = 0;
        if (addonstore.cache[index] != null) {
            this.reservations[index].Services.forEach((s) => {
                let foundAddon = addonstore.cache[index].Addons.filter(a => a.Code === s.ServiceCode)[0];
                if (foundAddon == null)  {
                    return;
                }

                let foundAddonDate = foundAddon.AddonDates.filter(d => d.DateData.length === 0 || d.DateData === s.Date);
                if (foundAddonDate.length === 0) {
                    NCB.Log.customError("checkoutPage.render(): Could not find addon date", {foundAddon: toJS(foundAddon), service: toJS(s)});
                } else {
                    let foundAddonTime = foundAddonDate[0].AddonTimes.filter(t => t.TimeData.length === 0 || t.TimeData === s.StartTime);
                    if (foundAddonTime.length === 0) {
                        NCB.Log.customError("checkoutPage.render(): Could not find addon time", {time: s.StartTime, addon: s});
                    } else {
                        let addonTotal = NCB.AddonHelper.getTotalPriceFromType(foundAddonTime[0].Price, s.Guests, numNights);
                        totalPrice += addonTotal;
                    }
                }                    
            });
        }

        return totalPrice;
    }

    /* Match cart room to cached room */
    getRoomPrice = (roomIndex: number): number => {

        if (roomstore.cache[roomIndex] == null || this.reservations[roomIndex] == null)
            return -1;

        let cart = this.reservations[roomIndex].Rooms[0];

        let rc = roomstore.cache[roomIndex].Rooms.filter(x => {
            return x.RatePlan.Code === cart.RatePlan && x.RoomType.Code === cart.RoomType;
        });

        if (rc.length === 1)
            return rc[0].Price.AmountAfterTax;
        else if (rc.length > 1)
            NCB.Log.customError("appstate.getRoomPrice: multiple candidates", { rate: cart.RatePlan, room: cart.RoomType });

        return -1;
    }

    @action addService = (roomIndex: number, service: CC.Library.Models.Shared.NcbServiceRq, calculatedPricePerPerson: number) => {
        let quantity = 0;
        service.Guests.forEach(g => {
            quantity += g.Count;
        });

        if (appstate.appMode === IAppMode.Wizard)
            NCB.Metrics.handleAddToCart(roomIndex, service.ServiceCode, service.ServiceCode, service.ServiceCode, service.ServiceCode, service.ServiceCode, calculatedPricePerPerson, quantity)

        console.log("addService", service);

        this.reservations[roomIndex].Services.push(service);
    }

    /**
     * @param code Addon code to remove
     * @param time Optional time identifier. If time is not defined, all addons with the matching code will be removed
     * @return Success if addon was found and removed. Fail if the addon code was not found or could not be removed
     */
    @action removeService = (roomIndex: number, code: string, time?: string, date?: string): boolean => {
        let res = this.reservations[roomIndex];
        if (res.Services.length === 0)
            return false;

        let success = false;

        // Iterate the array backwards since we will be splicing
        let i = res.Services.length;
        while (i--) {
            let service = res.Services[i];

            if (service.ServiceCode === code) {
                if (date == null || service.Date === date) {
                    if (time == null || service.StartTime == time) {
                        res.Services.splice(i, 1);
                        success = true;
                    }
                }
            }
        }

        return success;
    }

    @action applyVerifiedPromotionCode(promotionCode: string, newRoomRates: CC.Library.Models.Availability.AvailabilityRoomRate[]) {

        for (let i=0; this.reservations.length > i; i++) {
            console.log("appstate.applyVerifiedPromotionCode()", promotionCode, newRoomRates[i].RatePlan.Code, newRoomRates[i].RoomType.Code);
            
            this.reservations[i].Rooms[0].PromotionCode = promotionCode;
            this.reservations[i].Rooms[0].RatePlan = newRoomRates[i].RatePlan.Code;
            this.reservations[i].Rooms[0].RoomType = newRoomRates[i].RoomType.Code;
        }

        appstate.promotionCode = promotionCode;

        if (this.reservations.length > 1)
            this.doShowRoomDetails = false;
    }

    @action
    toggleRoomDetails(mode: boolean) {
        this.doShowRoomDetails = mode;

        if (!mode)
            this.doScrollToTop = true;
    }

    @computed
    get virtualAddonRooms(): CC.Library.Models.Availability.AvailabilityRoomRate[] {

        if (roomstore.isLoading || roomstore.cache[this.roomIndex] == null)
            return [];

        let configuredRooms = NCB.Settings.FeatureMatrixConfiguration.Rooms.filter(x => x.UpgradeAsAddon && x.GroupId === this.roomGroupCode.Id).map(x => x.Code);
        
        return roomstore.cache[this.roomIndex].Rooms
                    .filter(x => configuredRooms.indexOf(x.RoomType.Code) > -1)
                    .filter(x => x.RatePlan.Code === this.rateCode)
                    .filter(x => {
                        // Is this the current room?
                        if (x.RoomType.Code === this.roomTypeCode)
                            return true;    

                        // Make sure that this room is a direct descendant of the virtual addon
                        let p = NCB.RoomHelpers.getRoomCodeParents(x.RoomType.Code);
                        return p.indexOf(this.roomTypeCode) > -1;
                    });
    } 

    /**
     * Check if the current rooms guarantee code matches another rate
     */
    canSelectPackage = (rateCode: string): boolean => {
        let cachedRoom = this.getCachedRoom(this.roomIndex, rateCode, this.roomTypeCode, true);

        if (cachedRoom == null)
            return false;

        let isMatch = true;

        let newGuaranteeCode = cachedRoom.RatePlan.Guarantee.Code;
        
        for (let i=0; this.reservations.length > i; i++) {
            if (i !== this.roomIndex && roomstore.cache[i] != null) {
                let compareGuaranteCode = this.getGuaranteeCode(i);
                if (compareGuaranteCode !== newGuaranteeCode)
                    isMatch = false;
            }
        }

        return isMatch;
    }

    /**
     * Handle multiple rooms
     * Verify that the new rate code has the same guarantee code as the rest of the rooms
     * If not, try to update the other rooms with the same rate that is passed to this functions
     * 
     */
    @action
    selectPackage = (rateCode: string, showWarning: boolean) => {

        if (this.reservations.length > 1) {

            let cachedRoom = this.getCachedRoom(this.roomIndex, rateCode, this.roomTypeCode, true);

            if (cachedRoom != null) {
                let newGuaranteeCode = cachedRoom.RatePlan.Guarantee.Code;
                let isMatch = true;
                for (let i=0; this.reservations.length > i; i++) {
                    if (i !== this.roomIndex && roomstore.cache[i] != null) {
                        let compareGuaranteCode = this.getGuaranteeCode(i);
                        if (compareGuaranteCode !== newGuaranteeCode)
                            isMatch = false;
                    }
                }

                // Try to change all rooms to this rate
                // If the rate is not available, try to match the guarantee code
                if (this.reservations.length > 1 && !isMatch) {

                    if (showWarning) {
                        if (!confirm("This will change the rate for all rooms")) {
                            return;
                        }
                    }


                    for (let i=0; roomstore.cache.length > i; i++) {
                        if (i !== this.roomIndex) { // Skip current room
                            let cachedRoom = this.getCachedRoom(i);
                            if (cachedRoom != null && newGuaranteeCode !== cachedRoom.RatePlan.Guarantee.Code) {
                                // This Room's Guarantee Code does not match the new Rate's Guarantee Code
                                // Try to select a new Rate for this room
                                let roomCandidates = roomstore.cache[i].Rooms.filter(x => x.RatePlan.Guarantee.Code === newGuaranteeCode);
                                let exactCandidates = roomCandidates.filter(x => x.RatePlan.Code === rateCode && x.RoomType.Code === cachedRoom.RoomType.Code);
                                let exactMatchFound = (exactCandidates.length > 0);
                                if (exactMatchFound) {
                                    roomCandidates = exactCandidates;
                                } 
                                
                                let roomReservation = this.reservations[i];
                                let room = roomReservation.Rooms[0];

                                if (roomReservation != null) {
                                    /* Generate a message to the guest what is about to happen
                                    let exactRoomFound = room.RoomType === cachedRoom.RoomType.Code;
                                    let exactRateFound = room.RatePlan === cachedRoom.RatePlan.Code;
                                    let message = "Room " + (i + 1) + "\n";
                                    if (!exactMatchFound) {
                                        if (!exactRateFound)
                                            message += "Rate changed to " + roomCandidates[0].RatePlan.Code + "\n";
                                        if (!exactRoomFound)
                                            message += "Room changed to " + roomCandidates[0].RoomType.Code + "\n";
                                    }

                                    message += "Price changed to " + roomCandidates[0].Price.AmountAfterTax;

                                    alert(message);
                                    */

                                    roomReservation.Rooms[0].PromotionCode = room.PromotionCode;
                                    roomReservation.Rooms[0].StartDate = room.StartDate;
                                    roomReservation.Rooms[0].EndDate = room.EndDate;
                                    roomReservation.Rooms[0].RatePlan = roomCandidates[0].RatePlan.Code;
                                    roomReservation.Rooms[0].RoomType = roomCandidates[0].RoomType.Code;
                                    roomReservation.Rooms[0].Guests = room.Guests;
                                }
                            }
                        }
                    }
                }
            } else {
                NCB.Log.customError("roomPage.selectPackage: could not find cached room", {roomIndex: this.roomIndex, requestRate: rateCode})
            }
        }

        // Make sure that the selected room is available for this rate, otherwise select a new room type
        let foundRoomRates = roomstore.cache[this.roomIndex].Rooms.filter(x => x.RatePlan.Code === rateCode && x.RoomType.Code === this.roomTypeCode);
        if (foundRoomRates.length > 0) {
            this.setRate(this.roomIndex, rateCode);
        } else {
            let roomCandidates = roomstore.cache[this.roomIndex].Rooms.filter(x => x.RatePlan.Code === rateCode).sort((a, b) => a.Price.AmountAfterTax - b.Price.AmountAfterTax);
            let newRoomRate = roomCandidates[0];
            if (newRoomRate == null) {
                NCB.Log.customError("roomPage.selectPackage: Could not change to rate", {rateCode: rateCode});
            } else {
                this.setRoom(this.roomIndex, newRoomRate.RoomType.Code, newRoomRate.RatePlan.Code);
            }
        }
    }

    wizardDidUpdate() {
        
        if (this.doScrollToTop)
            appstate.navigateToHeader();

        this.doScrollToTop = false;
    }

    getLinkForCart(promotionCode?: string, rateCode?: string, startDate?: string, endDate?: string) {
        let p:string[] = [];

        p.push("promotionCode=" + (promotionCode == null ? this.promotionCode : promotionCode));
        p.push("startDate=" + (startDate != null ? startDate : NCB.DateUtils.getISODate(this.startDate)));
        p.push("endDate=" +  (endDate != null ? endDate : NCB.DateUtils.getISODate(this.endDate)));

        if (rateCode != null) {
            p.push("rate=" + rateCode);
        }

        this.reservations.forEach(x => {
            let a = NCB.Utils.getNumAdults(x.Rooms[0].Guests);
            let c = NCB.Utils.getNumChildren(x.Rooms[0].Guests);

            p.push("room=" + a + "_" + c);
        });

        return NCB.Settings.BookingStartURL + "?" + p.join("&");
    }

    getIntialStateFromUrl(): IInitialWizardState  {
        let initialStartDate: Date = null;
        let initialEndDate: Date = null;
        let initialRooms: CC.Library.Models.Calendar.NcbCalendarRoom[] = [];
        let initialPromotionCode: string = "";
        let initialGroupCode: string = "";
        let childAges: number[][] = [];
        let forceShowCalendar: boolean = false;
        
        location.search.substr(1).split("&").forEach(function(item) {
            let s = item.split("=");
            let key = s[0];
            let value = s[1];

            switch (key) {
                case "startDate":
                    if (value != null && value.length > 6) {
                        try {
                            initialStartDate = new Date(Date.parse(value));
                        } catch(e) {
                            NCB.Log.customError("getIntialStateFromUrl(): could not parse start date from URL", location.search);
                            initialStartDate = null;
                        }
                    }
                    
                    break;

                case "endDate":
                    if (value != null && value.length > 6) {
                        try {
                            initialEndDate = new Date(Date.parse(value));
                        } catch(e) {
                            NCB.Log.customError("getIntialStateFromUrl(): could not parse end date from URL", location.search);
                            initialEndDate = null;
                        }
                    }
                    
                    break;

                case "room":
                    // Legacy support with the ":" separator
                    let rsplit = (value.indexOf(':') > -1) ? value.split(":") : value.split("_");

                    let guests: CC.Library.Models.Shared.NcbGuestCount[] = [];
                    guests.push({
                        GuestType: CC.Library.Models.Shared.GuestType.Adult,
                        Count: parseInt(rsplit[0]),
                        Age: null
                    });

                    let numChildren = parseInt(rsplit[1]);

                    if (rsplit[1] != null && !isNaN(numChildren)) {
                        for (let i=0; numChildren > i; i++) {

                            guests.push({
                                GuestType: CC.Library.Models.Shared.GuestType.Child,
                                Count: 1,
                                Age: null
                            });
                        }
                    }

                    initialRooms.push({
                        Guests: guests
                    });
                    break;

                case "promotionCode":
                    initialPromotionCode = value;
                    break;

                case "group":
                case "rate":
                case "ratePlanOrGroupCode":
                    if (value != null && value.length > 0)
                        initialGroupCode = value;
                    break;

                case "ages":
                    // Legacy support with the ":" separator
                    let splitChildAges = (value.indexOf(':') > -1) ? value.split(":") : value.split("_");
                    childAges.push(splitChildAges.map(x => parseInt(x)));
                    break;

                case "showCalendar":
                case "showcalendar":
                    if (value != null) {
                        forceShowCalendar = value.trim().toLocaleLowerCase() === "true";
                    }
                    
                    break;
            }
        });

        if (childAges.length > 0) {
            let ri = 0;
            initialRooms.forEach(room => {
                let i = 0;
                if (childAges[ri] != null && childAges[ri].length > 0) {
                    room.Guests.filter(x => x.GuestType === CC.Library.Models.Shared.GuestType.Child).forEach(x => {
                        x.Age = childAges[ri][i]; 
                        i++;
                    });
                }
                ri++;
            });
        }

        // Did we founda any rooms?
        if (initialRooms.length === 0) {
            initialRooms = [{
                Guests: [
                    {
                        GuestType: CC.Library.Models.Shared.GuestType.Adult,
                        Count: NCB.Settings.HotelProperties.DefaultNumberOfAdults,
                        Age: null
                    }
                ]
            }];
        }

        // console.log("INITIAL ROOMS", initialRooms);

        if (initialStartDate == null)
            initialEndDate = null;

        if (initialEndDate == null)
            initialStartDate = null;

        return {
            startDate: initialStartDate,
            endDate: initialEndDate,
            rooms: initialRooms,
            showCalendar: forceShowCalendar || initialStartDate == null,
            promotionCode: initialPromotionCode,
            groupCode: initialGroupCode
        }
    }

    changeAllRatesToNonLoyaltyRates() {
        let roomIndex = 0;
        this.reservations.forEach(r => {
            let currentRoom = r.Rooms[0];
            let currentRate = currentRoom.RatePlan;
            let currentFeature = NCB.FeatureMatrix.getRate(currentRate);
            
            if (currentFeature.IsLoyaltyRate) {
                let barRates = NCB.Settings.FeatureMatrixConfiguration.Rates.filter(x => x.ChoiceClubRate === currentRate);
                let barRatesList: string[] = [];
                barRates.forEach(br => {
                    br.Codes.forEach(x => barRatesList.push(x));
                });

                // Filter from available rooms
                let availableRate = roomstore.cache[roomIndex].Rooms.filter(x => x.RoomType.Code == currentRoom.RoomType && barRatesList.indexOf(x.RatePlan.Code) > -1)[0];

                if (availableRate != null) {
                    this.setRoom(roomIndex, currentRoom.RoomType,  availableRate.RatePlan.Code);
                } else {
                    console.error("Could not change to non-loyalty rate for ", currentRate);
                }
            }

            roomIndex++;
        });
    }

    @action
    toggleLoyaltyOptOut(newState: boolean) {
        this.loyaltyOptOut = newState;
    }
}

const appstate = new AppState();

export default appstate;


// WEBPACK FOOTER //
// ./src/appstate.ts